import {
	NODE_CATEGORIES,
	NODE_TYPE_LABELS,
	type NodeType,
	type PortDef,
} from "@velizhanin-dev/bot-scenario"
import { Handle, type NodeProps, Position } from "@xyflow/react"
import { Link2, Paperclip } from "lucide-react"
import { memo } from "react"
import {
	type BlockNode,
	CATEGORY_APPEARANCE,
	attachmentLabel,
	NODE_ICONS,
	NODE_WIDTH,
	TARGET_HANDLE_ID,
} from "@/entities/bot-builder"
import { cn } from "@/shared/lib/utils"

function portLabel(port: PortDef): string {
	if (port.kind === "next") return "Дальше"
	if (port.kind === "text") return "Ввод с клавиатуры"
	return port.label
}

const HANDLE_CLASS =
	"!size-3 !border-2 !border-card !bg-muted-foreground/70 transition-colors hover:!bg-primary"

/** Высота шапки блока: вход связи стоит по её центру. */
const HEADER_CENTER = 27

/**
 * Блок на холсте в стиле карточек панели: иконка категории, название и тип, краткое
 * содержимое, кнопки и выходы строками с точкой справа.
 */
export const BlockNodeView = memo(function BlockNodeView({ data, selected }: NodeProps<BlockNode>) {
	const { node, summary, ports, issues, active, stats } = data
	const type = node.type as NodeType
	const appearance = CATEGORY_APPEARANCE[NODE_CATEGORIES[type] ?? "action"]
	const Icon = NODE_ICONS[type]
	const errors = issues.filter((issue) => issue.severity === "error").length
	const warnings = issues.length - errors
	const typeLabel = NODE_TYPE_LABELS[type] ?? node.type
	const note = node.note?.trim()
	const title = type === "start" ? "Старт" : note || typeLabel
	const subtitle = type === "start" ? "Отсюда начинается воронка" : note ? typeLabel : null
	const buttons = ports.filter((port) => port.kind === "button")
	const outputs = ports.filter((port) => port.kind !== "button")
	const attachment = type === "start" ? null : attachmentLabel(node.config ?? {})

	return (
		<div
			style={{ width: NODE_WIDTH }}
			className={cn(
				"relative rounded-2xl border bg-card text-card-foreground shadow-[0_10px_30px_-18px_rgba(0,0,0,0.9)] transition-[border-color,box-shadow] duration-150",
				selected
					? "border-primary/70 ring-4 ring-primary/15"
					: errors > 0
						? "border-destructive/60"
						: "border-border hover:border-foreground/20",
				active && "ring-4 ring-emerald-400/30",
			)}
		>
			{type !== "start" ? (
				<Handle
					type="target"
					id={TARGET_HANDLE_ID}
					position={Position.Left}
					style={{ top: HEADER_CENTER }}
					className={HANDLE_CLASS}
				/>
			) : null}

			<div className="flex items-center gap-2.5 px-3 pt-3 pb-2.5">
				<span
					className={cn("grid size-[30px] shrink-0 place-items-center rounded-lg", appearance.tile)}
				>
					{Icon ? <Icon className="size-4" /> : null}
				</span>
				<div className="min-w-0 flex-1">
					<p className="truncate text-[13px] leading-4 font-semibold">{title}</p>
					{subtitle ? (
						<p className="truncate text-[11px] leading-4 text-muted-foreground">{subtitle}</p>
					) : null}
				</div>
				{errors > 0 ? (
					<span
						title="Ошибки"
						className="rounded-full bg-destructive/15 px-1.5 text-[11px] leading-5 font-semibold text-red-300 tabular-nums"
					>
						{errors}
					</span>
				) : warnings > 0 ? (
					<span
						title="Предупреждения"
						className="rounded-full bg-amber-400/15 px-1.5 text-[11px] leading-5 font-semibold text-amber-300 tabular-nums"
					>
						{warnings}
					</span>
				) : null}
			</div>

			{stats && type !== "start" ? (
				<div className="mx-3 mb-2.5 grid grid-cols-2 divide-x divide-border rounded-lg border border-border text-center">
					{(
						[
							["passed", stats.passed, "Прошли"],
							["stuck", stats.stuck, type === "wait" ? "Ожидают" : "Застряли"],
						] as const
					).map(([mode, count, caption]) => {
						const label = summary.replace(/\s+/g, " ").slice(0, 40) || node.id
						const body = (
							<>
								<span className="block text-sm font-semibold tabular-nums">{count}</span>
								<span className="text-[11px] text-muted-foreground">{caption}</span>
							</>
						)
						return stats.openPeople && count > 0 ? (
							<button
								key={mode}
								type="button"
								title="Показать людей"
								className="nodrag nopan cursor-pointer rounded-md py-1.5 transition-colors hover:bg-accent"
								onClick={(event) => {
									event.stopPropagation()
									stats.openPeople?.(mode, label)
								}}
							>
								{body}
							</button>
						) : (
							<span key={mode} className="py-1.5">
								{body}
							</span>
						)
					})}
				</div>
			) : null}

			{type !== "start" && summary ? (
				<div className="mx-3 mb-3 line-clamp-4 rounded-lg bg-secondary/50 px-2.5 py-2 text-xs leading-snug break-words text-foreground/85">
					{summary}
				</div>
			) : null}

			{attachment ? (
				<div
					title="К блоку прикреплён файл"
					className="mx-3 mb-3 flex items-center gap-1.5 text-[11px] leading-4 text-muted-foreground"
				>
					<Paperclip className="size-3 shrink-0" aria-hidden />
					<span className="truncate">{attachment}</span>
				</div>
			) : null}

			{buttons.length > 0 ? (
				<div className="space-y-1.5 px-3 pb-3">
					{buttons.map((port) => (
						<div
							key={port.id}
							className="relative flex h-8 items-center justify-center rounded-lg border border-border bg-secondary/40 px-7 text-xs text-foreground/85"
						>
							<Link2 className="absolute left-2.5 size-3 text-muted-foreground" aria-hidden />
							<span className="truncate">{port.label}</span>
							{stats ? (
								<span
									title="Нажали"
									className="absolute right-2.5 rounded-full bg-primary/15 px-1.5 text-[10px] leading-4 font-semibold text-primary tabular-nums"
								>
									{stats.clicks[port.id] ?? 0}
								</span>
							) : null}
							<Handle
								type="source"
								id={port.id}
								position={Position.Right}
								style={{ right: -13 }}
								className={HANDLE_CLASS}
							/>
						</div>
					))}
				</div>
			) : null}

			{outputs.map((port) => (
				<div
					key={port.id}
					className="relative flex h-9 items-center border-t border-border px-3 text-xs text-muted-foreground"
				>
					<span className="truncate pr-4">{portLabel(port)}</span>
					<Handle type="source" id={port.id} position={Position.Right} className={HANDLE_CLASS} />
				</div>
			))}
		</div>
	)
})
